import { useCallback, useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import {
  getArrivals,
  getDepartures,
  getSingleFlight,
  linkTransfersAndCounts,
} from "../api/flightsApi.js";

/**
 * Flights hook for Transfers feature
 * - Owns arrivals/departures maps
 * - Handles initial fetch on date change
 * - Handles hourly refresh + lastUpdate label
 */
export default function useFlights(selectedDate) {
  const [arrivals, setArrivals] = useState([]);
  const [departures, setDepartures] = useState([]);
  const [lastUpdate, setLastUpdate] = useState("");

  // If selectedDate is a dayjs object, this will be stable and avoid re-formatting everywhere
  const dateStr = useMemo(
    () => (selectedDate ? selectedDate.format("YYYY-MM-DD") : dayjs().format("YYYY-MM-DD")),
    [selectedDate]
  );

  const fetchArrivalsOnDate = useCallback(
    // updateExisting defaults to FALSE. It used to default to true, so merely
    // opening the tab asked the server to copy flight-service estimates over
    // transfer times somebody had corrected by hand. Callers that genuinely
    // want enrichment pass true explicitly.
    async (date = selectedDate, updateExisting = false) => {
      const formattedDate = (date || dayjs()).format("YYYY-MM-DD");
      try {
        const data = await getArrivals(formattedDate, { updateExisting });
        sessionStorage.setItem("flights", JSON.stringify(data));
        setLastUpdate(dayjs().format("L LT"));
        setArrivals(data);
        return data;
      } catch (e) {
        console.error("[useFlights] arrivals fetch failed:", e);
        return {};
      }
    },
    [selectedDate]
  );

  const fetchDeparturesOnDate = useCallback(
    // updateExisting defaults to FALSE. It used to default to true, so merely
    // opening the tab asked the server to copy flight-service estimates over
    // transfer times somebody had corrected by hand. Callers that genuinely
    // want enrichment pass true explicitly.
    async (date = selectedDate, updateExisting = false) => {
      const formattedDate = (date || dayjs()).format("YYYY-MM-DD");
      try {
        const data = await getDepartures(formattedDate, { updateExisting });
        sessionStorage.setItem("departures", JSON.stringify(data));
        setLastUpdate(dayjs().format("L LT"));
        setDepartures(data || {});
        return data;
      } catch (e) {
        console.error("[useFlights] departures fetch failed:", e);
        return {};
      }
    },
    [selectedDate]
  );

  const fetchAndLinkArrivals = useCallback(
    async (date = selectedDate) => {
      const formattedDate = (date || dayjs()).format("YYYY-MM-DD");
      try {
        await linkTransfersAndCounts(formattedDate);
      } catch (e) {
        console.error("[useFlights] linkTransfersAndCounts failed:", e);
        // still continue to fetch arrivals
      }
      return fetchArrivalsOnDate(date);
    },
    [selectedDate, fetchArrivalsOnDate]
  );

  // Update one flight entry in arrivals map after fetching single flight
  const updateArrivalFlight = useCallback((flightNumber, sdata) => {
    setArrivals((prev) => {
      // prev is usually an object keyed by flight number
      const next = { ...(prev || {}) };
      next[flightNumber] = sdata?.[flightNumber];
      return next;
    });
  }, []);

  const fetchSingleFlight = useCallback(
    async (flightNo, flightDate) => {
      try {
        const sdata = await getSingleFlight(flightNo, flightDate);
        sessionStorage.setItem("singleFlight", JSON.stringify(sdata));
        updateArrivalFlight(flightNo, sdata);
        return sdata;
      } catch (e) {
        console.error("[useFlights] single flight fetch failed:", e);
        return null;
      }
    },
    [updateArrivalFlight]
  );

  // Initial fetch when date changes
  useEffect(() => {
    // keep behaviour identical to your container: fetch both maps for selected date
    fetchArrivalsOnDate(selectedDate);
    fetchDeparturesOnDate(selectedDate);
    // lastUpdate is only set by the hourly interval today (so we keep that behaviour)
  }, [fetchArrivalsOnDate, fetchDeparturesOnDate, selectedDate]);

  // The hourly background refresh has been removed. It re-fetched arrivals and
  // departures every hour for as long as the tab stayed open, and because those
  // fetches asked the server to update transfers, a page left open overnight
  // rewrote operational data nobody was looking at. Nothing about a transfer
  // list needs to poll a flight API in the background; where enrichment is
  // wanted it should be a button somebody presses.
  //
  // lastUpdate was only ever set by that interval. It is now stamped whenever a
  // fetch actually succeeds, so the label means "this list was loaded at" rather
  // than "a background poll ran at" - which is both more honest and more useful,
  // and stops it sitting permanently blank now that nothing polls.

  return {
    arrivals,
    departures,
    lastUpdate,
    dateStr,

    // actions
    fetchArrivalsOnDate,
    fetchDeparturesOnDate,
    fetchAndLinkArrivals,
    fetchSingleFlight,
    setArrivals,      // optional escape hatch (can remove later)
    setDepartures,    // optional escape hatch (can remove later)
  };
}
